import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  styled,
  Pagination,
  Stack,
  Breadcrumbs,
  Link as MUILink,
  Divider,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  getAllSellerCategories,
  createSellerCategory,
  updateSellerCategory,
  deleteSellerCategory,
} from "../redux/slices/sellerCategoriesSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import PageTitle from "../Components/PageTitle";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// Child Component
const ProductCategory = ({ data, onEdit, onDelete }) => {
  const title = data.name || "Category Name";
  const gst = data.gst ? `${data.gst}%` : "0%";
  const image =
    data.image ||
    "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?auto=format&fit=crop&w=600&q=80";
  const hsnCode = data.hsnCode || "";
  return (
    <Card
      sx={{
        borderRadius: 4,
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
        position: "relative",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <Box sx={{ position: "relative" }}>
        <CardMedia
          component="img"
          height="180"
          image={image}
          alt={title}
          sx={{ filter: "brightness(0.9)", objectFit: "cover" }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            background:
              "linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)",
            pt: 8,
            pb: 2,
            px: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#fff",
              fontWeight: "bold",
              textTransform: "capitalize",
            }}
          >
            {title}
          </Typography>
          {/* <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>{data.productCount || 0} Products</Typography> */}
        </Box>
      </Box>

      <CardContent sx={{ pb: "16px !important" }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              sx={{ color: "#9e9e9e", fontWeight: 600 }}
            >
              HSN CODE
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
            >
              {hsnCode || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              sx={{ color: "#9e9e9e", fontWeight: 600 }}
            >
              GST RATE
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
            >
              {gst}
            </Typography>
          </Grid>
        </Grid>

        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Button
              variant="contained"
              disableElevation
              onClick={() => onEdit(data)}
              sx={{
                textTransform: "none",
                bgcolor: "#ede7f6",
                color: "#0b4064",
                fontWeight: "bold",
                borderRadius: 2,
                px: 2,
                "&:hover": { bgcolor: "#09324d", color: "#fff" },
              }}
            >
              Edit
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              disableElevation
              onClick={() => onDelete(data)}
              sx={{
                textTransform: "none",
                bgcolor: "#ede7f6",
                color: "#b1356bff",
                fontWeight: "bold",
                borderRadius: 2,
                px: 2,
                "&:hover": { bgcolor: "#d1c4e9" },
              }}
            >
              Delete
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Parent Component
export default function CategoryShowcase() {
  const dispatch = useDispatch();
  const { categories, page, total, totalPages } = useSelector(
    (state) => state.sellerCategory,
  );
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    gst: "",
    hsnCode: "",
    image: null,
    imagePreview: null,
  });

  useEffect(() => {
    dispatch(getAllSellerCategories({ page: page || 1 }));
  }, [dispatch, page]);

  // Page Change Handler
  const handlePageChange = (event, value) => {
    dispatch(getAllSellerCategories({ page: value }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --- Handlers ---
  const handleOpen = (cat = null) => {
    if (cat) {
      setSelectedCategory(cat);
      setFormData({
        name: cat.name,
        gst: cat.gst,
        image: cat.image,
        imagePreview: cat.image,
        hsnCode: cat.hsnCode,
      });
    } else {
      setSelectedCategory(null);
      setFormData({
        name: "",
        gst: "",
        hsnCode: "",
        image: null,
        imagePreview: null,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDeleteDialogOpen(false);
    setFormData({
      name: "",
      gst: "",
      hsnCode: "",
      image: null,
      imagePreview: null,
    });
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () =>
      setFormData({
        ...formData,
        image: reader.result,
        imagePreview: reader.result,
      });
    reader.readAsDataURL(file);
  };

  // --- Create API ---
  const handleSubmit = () => {
    const payload = {
      name: formData.name,
      gst: formData.gst,
      image: formData.image,
      hsnCode: formData.hsnCode,
    };

    if (selectedCategory) {
      dispatch(
        updateSellerCategory({
          id: selectedCategory._id,
          data: payload,
        }),
      )
        .unwrap()
        .then(() => {
          toast.success("Product Category Updated!");
          handleClose();
          dispatch(getAllSellerCategories({ page: page || 1 }));
        })
        .catch((err) => {
          toast.error(err.message);
        });
    } else {
      // --- CREATE API ---
      dispatch(createSellerCategory(payload))
        .unwrap()
        .then(() => {
          toast.success("Product Category Added!");
          handleClose();
          dispatch(getAllSellerCategories({ page: page || 1 }));
        })
        .catch((err) => toast.error(err.message));
    }
  };
  //  --- Delete API ---
  const handleDeleteConfirm = () => {
    dispatch(deleteSellerCategory(selectedCategory._id))
      .unwrap()
      .then(() => {
        toast.success("Product Category Deleted!");
        handleClose();
        dispatch(getAllSellerCategories({ page: page || 1 }));
      })
      .catch((err) => toast.error(err.message));
  };

  return (
    <>
    <PageTitle
  title="Manage Product Categories - BizBridge"
  description="Create and manage product categories with GST rates and HSN codes to streamline your B2B product listings on BizBridge."
  keywords="B2B categories, product category management, GST rate, HSN code, seller panel, BizBridge products"
  url="/seller-dashboard/product-category"
/>

    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background: "#f8fafc",
        py: { xs: 1, sm: 2 },
      }}
    >
      {/* Breadcrumb */}
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          width: "fit-content",
          backgroundColor: "#fff",
          ml: { xs: 1, sm: 2.9 },
          padding: "13px",
          borderRadius: "12px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <MUILink
          component={Link}
          to="/seller-dashboard/buyer-category"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Buyer Category
        </MUILink>
        <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
          Product Category
        </Typography>
      </Breadcrumbs>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ maxWidth: 1350, mx: "auto", px: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
            Product Categories
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => handleOpen()}
            sx={{
              textTransform: "none",
              borderRadius: 3,
              px: 3,
              py: 1,
              fontSize: 15,
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#09324d",
              },
            }}
          >
            Add Category
          </Button>
        </Box>

        <Grid container spacing={3}>
          {categories?.map((cat) => (
            <Grid item key={cat._id} xs={12} sm={12} md={6} lg={4}>
              <ProductCategory
                data={cat}
                onEdit={(data) => handleOpen(data)}
                onDelete={(data) => {
                  setSelectedCategory(data);
                  setDeleteDialogOpen(true);
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* 5. Pagination UI */}
      {totalPages > 1 && (
        <Stack spacing={2} alignItems="center" sx={{ mt: 5, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            shape="rounded"
            showFirstButton
            showLastButton
          />
          <Typography variant="caption" color="textSecondary">
            Showing page {page} of {totalPages}
          </Typography>
        </Stack>
      )}

      {/* --- ADD/EDIT MODAL --- */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6" fontWeight="bold">
            {selectedCategory ? "Edit Category" : "Add Category"}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            margin="normal"
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            fullWidth
            label="GST (%)"
            name="gst"
            type="number"
            value={formData.gst}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            fullWidth
            label="HSN Code"
            name="hsnCode"
            type="number"
            value={formData.hsnCode}
            onChange={handleChange}
          />

          <Button
            component="label"
            variant="outlined"
            fullWidth
            startIcon={<CloudUploadIcon />}
            sx={{ mt: 2, py: 2, borderStyle: "dashed" }}
          >
            {formData.image ? "Change Image" : "Upload Image"}
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
          </Button>
          {formData.imagePreview && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <img
                src={formData.imagePreview}
                alt="Preview"
                style={{ maxHeight: 120, borderRadius: 8 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ bgcolor: "#0b4064" }}
          >
            {selectedCategory ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- DELETE CONFIRMATION --- */}
      <Dialog open={deleteDialogOpen} onClose={handleClose}>
        <Box sx={{ p: 3, textAlign: "center" }}>
          <WarningAmberIcon sx={{ fontSize: 50, color: "#ef4444", mb: 2 }} />
          <Typography variant="h6" fontWeight="bold">
            Are you sure?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Do you really want to delete <b>{selectedCategory?.name}</b>?
          </Typography>
          <Box
            sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "center" }}
          >
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
            >
              Yes, Delete
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
    </>
  );
}
